import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { DxFormComponent } from 'devextreme-angular';
import {
  CaisseTypeAuthentification,
  ClassificationValueDto,
  ClassificationValuesLexiClient,
  CumulScanSurUnArticle,
  DocumentType,
  ElevationPrivilegeAuthentificationType,
  GetSiteListDto,
  LexiMouvementTypeDto,
  LexiMouvementTypesLexiClient,
  LicenceLexiClient,
  LieuStockageDto,
  LieuStockagesLexiClient,
  OptionPrelevementClotureCaisse,
  PieceDetailTpvDto,
  ProfilPartenaireDto,
  ProfilPartenairesLexiClient,
  SearchType,
  SelectionObligatoireClientSettings,
  SitesLexiClient,
  TaxeDto,
  TaxesLexiClient,
  TpvSettings,
  TypeRemiseDto,
  TypeRemisesLexiClient,
  UpdateLexiClient
} from '@lexi-clients/lexi';
import DataSource from 'devextreme/data/data_source';
import { AuthService } from "lexi-angular/src/app/settings/auth.service";
import { DxDataSourceService } from 'lexi-angular/src/app/shared/services/dx-data-source.service';
import { forkJoin, lastValueFrom } from 'rxjs';
import { TriProperty } from '../configuration-tri/configuration-tri.component';
import notify from 'devextreme/ui/notify';
import { NotificationType } from '../references/references';

/**
 * Interface étendue de PieceDetailTpvDto pour l'aperçu avec les champs supplémentaires
 */
interface PieceDetailApercuDto extends PieceDetailTpvDto {
  codeLieuStockage?: string | null;
  intituleLieuStockage?: string | null;
  codeArticleFournisseur?: string | null;
  uniteVenteIntitule?: string | null;
  uniteVenteCode?: string | null;
  codeSite?: string | null;
}

@Component({
  selector: 'app-tpv-settings-form',
  templateUrl: './tpv-settings-form.component.html',
  styleUrls: ['./tpv-settings-form.component.scss'],
  standalone: false
})
export class TpvSettingsFormComponent implements OnInit {
  @Input() colCount: number = 1;
  @Input() colSpan: number = 1;

  private _tpvSettings: TpvSettings = {};
  get tpvSettings(): TpvSettings {
    return this._tpvSettings;
  }
  @Input() set tpvSettings(value: TpvSettings) {
    this._tpvSettings = value;
    if (this._tpvSettings == null) {
      this._tpvSettings = {};
    }
    if (this.tpvSettings.selectionObligatoireClientSettings == null) {
      const dto: SelectionObligatoireClientSettings = {
        mouvementTypeCodeBoList: [],
        informationGarantie: false,
        informationSuiviSAV: false,
        classificationCodeBoList: []
      };
      this.tpvSettings.selectionObligatoireClientSettings = dto;
    }
    // Initialiser la configuration de tri si elle n'existe pas
    if (!this.tpvSettings.triHistoriqueTransactions) {
      this.tpvSettings.triHistoriqueTransactions = [];
    }
  }

  @Input() formIsReadOnly: boolean = false;
  lieuStockageDataSource: LieuStockageDto[] = [];

  typesMouvementDataSource: LexiMouvementTypeDto[] = [];
  classificationsValuesDataSource: ClassificationValueDto[] = [];
  profilsPartenairesDataSource : ProfilPartenaireDto[] = [];
  siteDataSource : GetSiteListDto[] = [];
  versionsTpvOptions : Object;
  private _siteId: number;
  get siteId(): number {
    return this._siteId;
  }

  @Input() set siteId(value: number) {
    if (value != null && value != undefined) {
      this._siteId = value;
      this.setLieuStockageDataSource();
    }
  }

  private _isLieuxStockageForSite: boolean;
  get isLieuxStockageForSite(): boolean {
    return this._isLieuxStockageForSite;
  }
  @Input() set isLieuxStockageForSite(value: boolean) {
    if (value != null) {
      this._isLieuxStockageForSite = value;
      if (value == false) this.setLieuStockageDataSource();
    }
  }

  readonly caisseTypeAuthentification = [
    { id: CaisseTypeAuthentification.domainWindows, intitule: "Domaine Windows" },
    { id: CaisseTypeAuthentification.localLexi, intitule: "Lexi local" },
    { id: CaisseTypeAuthentification.sessionWindows, intitule: "Session Windows" },
  ];

  readonly elevationPrivilegeAuthentificationType = [
    { id: ElevationPrivilegeAuthentificationType.badgeOnly, intitule: "Badge uniquement" },
    { id: ElevationPrivilegeAuthentificationType.badgeAndPassword, intitule: "Badge et code PIN" },
    { id: ElevationPrivilegeAuthentificationType.domainWindows, intitule: "Domaine Windows" },
  ];

  readonly cumulScanSurUnArticleDataSource = [
    { id: CumulScanSurUnArticle.forceNouvelleLigne, intitule: "Forcer sur une nouvelle ligne" },
    { id: CumulScanSurUnArticle.auChoix, intitule: "Au choix" },
    { id: CumulScanSurUnArticle.parDefautNouvelleLigne, intitule: "Par défaut: Nouvelle ligne" },
    { id: CumulScanSurUnArticle.parDefautCumul, intitule: "Par défaut : Cumul" },
  ];

  readonly optionPrelevementClotureCaisseDataSource = [
    { id: OptionPrelevementClotureCaisse.none, intitule: "Prélèvement libre" },
    { id: OptionPrelevementClotureCaisse.vider, intitule: "Vider la caisse" },
    { id: OptionPrelevementClotureCaisse.fondReference, intitule: "Atteindre le fond de référence" },
  ];

  readonly searchTypeDataSource = [
    { id: SearchType.contains, intitule: "Contient" },
    { id: SearchType.beginWith, intitule: "Commence par" },
    { id: SearchType.exact, intitule: "Exact" },
    { id: SearchType.endWith, intitule: "Termine par" },
  ];
  historiqueVenteTri : TriProperty[]  = [
    { propertyName : 'DateVente', propertyDisplay : 'Date de vente', dataType : 'date'},
    { propertyName : 'SiteIntitule', propertyDisplay : 'Site', dataType : 'string'},
    { propertyName : 'Numero', propertyDisplay : 'Numéro de pièce', dataType : 'string'},
    { propertyName : 'IsSolde', propertyDisplay : 'Est soldé', dataType : 'boolean'},
  ]

  // Objet d'exemple pour l'aperçu des patterns
  pieceDetailExemple: PieceDetailApercuDto = {
    codeArticle: 'ART-001',
    libelleLongArticle: 'Libellé article',
    codeLieuStockage: 'ENT-1',
    intituleLieuStockage: 'Entrepôt principal',
    codeArticleFournisseur: 'REF-FOURN-123',
    uniteVenteIntitule: 'Pied Carré',
    uniteVenteCode: 'ft²',
    codeSite: 'SITE-1',
    rapportVente: 2,
    rapportTarifaire: 1,
    quantite: 1,
    prixHt: 900,
    prixTtc: 1000
  };

  readonly colonnesPatternLignePanier = [
    { value: '{CodeArticle}', intitule: 'Code article', exemple : this.pieceDetailExemple.codeArticle },
    { value: '{IntituleLigne}', intitule: 'Intitulé de la Ligne (en général reprend l\'intitulé article, sauf pour les commentaire)', exemple : this.pieceDetailExemple.libelleLongArticle },
    { value: '{IntituleArticle}', intitule: 'Intitulé article', exemple : this.pieceDetailExemple.libelleLongArticle },
    { value: '{CodeLieuStockage}', intitule: 'Code lieu de stockage', exemple : this.pieceDetailExemple.codeLieuStockage },
    { value: '{IntituleLieuStockage}', intitule: 'Intitulé lieu de stockage', exemple : this.pieceDetailExemple.intituleLieuStockage },
    { value: '{CodeSite}', intitule: 'Code Site', exemple : this.pieceDetailExemple.codeSite },
    { value: '{CodeArticleFournisseur}', intitule: 'Référence article fournisseur', exemple : this.pieceDetailExemple.codeArticleFournisseur },
    { value: '{IntituleUniteVente}', intitule: 'Intitulé unité de vente', exemple : this.pieceDetailExemple.uniteVenteIntitule },
    { value: '{CodeUniteVente}', intitule: 'Code unité de vente', exemple : this.pieceDetailExemple.uniteVenteCode },
    { value: '{RapportVente}', intitule: 'Rapport de vente', exemple : this.pieceDetailExemple.rapportVente },
    { value: '{RapportTarifaire}', intitule: 'Rapport tarifaire', exemple : this.pieceDetailExemple.rapportTarifaire },
  ].sort((a, b) => a.intitule.localeCompare(b.intitule));

  partenaireDataSource: DataSource;
  typeRemises: TypeRemiseDto[];
  taxesDataSource: TaxeDto[] = [];

  @ViewChild(DxFormComponent) tpvForm: DxFormComponent;

  /** Texte par défaut proposé dans le message lorsque l'avertissement taxe est activé. */
  readonly defaultMessageAvertissementTaxe =
    "LIVRAISON OBLIGATOIRE.\n" +
    "En raison de l'application automatique de la taxe 1% sur le client {client},\n" +
    "Il est nécessaire qu'une preuve de transport (connaissement ou LTA) soit gardée.";

  isNiuora: boolean;

  constructor(
    private readonly lexiMouvementTypesLexiClient: LexiMouvementTypesLexiClient,
    private readonly dxDataSourceService: DxDataSourceService,
    private readonly typeRemisesLexiClient: TypeRemisesLexiClient,
    private readonly lieuStockageLexiClient: LieuStockagesLexiClient,
    private readonly updateCaisseLexiClient: UpdateLexiClient,
    private readonly classificationValuesLexiClient: ClassificationValuesLexiClient,
    private readonly profilPartenairesLexiClient: ProfilPartenairesLexiClient,
    private readonly sitesLexiClient: SitesLexiClient,
    private readonly authService: AuthService,
    private readonly licenceLexiClient: LicenceLexiClient,
    private readonly taxesLexiClient: TaxesLexiClient
  ) { }

  ngOnInit() {
    this.getAllDataSources();
  }

  async getAllDataSources(){
    const requestResults = await lastValueFrom(forkJoin({
      typeRemises : this.typeRemisesLexiClient.getAll(),
      typesMouvement : this.lexiMouvementTypesLexiClient.getMouvementType(DocumentType.vente),
      isNiuora : this.licenceLexiClient.isNiuora(),
      profilPartenaires : this.profilPartenairesLexiClient.getAll(),
      sites : this.sitesLexiClient.getAll(),
      taxes : this.taxesLexiClient.getAll()
    }))

    try {
      const versionsTpv = await lastValueFrom(this.updateCaisseLexiClient.getAllVersions());
      this.versionsTpvOptions = { items: versionsTpv.map(item => { return item.version }), searchEnabled: true }
    } catch {}

    this.typeRemises = requestResults.typeRemises;
    this.partenaireDataSource = this.dxDataSourceService.getPartenaireDataSourceForSelectBox(true);
    this.typesMouvementDataSource = requestResults.typesMouvement;
    this.classificationsValuesDataSource = this.authService?.currentSociete?.classificationComptableTypeId == null ? []
      : await lastValueFrom(this.classificationValuesLexiClient.getByClassificationTypeId(this.authService.currentSociete.classificationComptableTypeId));
    this.profilsPartenairesDataSource = requestResults.profilPartenaires;
    this.taxesDataSource = requestResults.taxes;
    this.isNiuora = requestResults.isNiuora;
    const getSiteListeDefault : GetSiteListDto = <GetSiteListDto>{
      id: 0,
      codeBo: "0",
      intitule: "Tous",
      societeId: 1,
      actif: true
    };
    this.siteDataSource.push(getSiteListeDefault);
    this.siteDataSource.push(...requestResults.sites);
  }

  partenaireEtTypesDisplayExpr(data) {
    return data && `${data.codeBo} - ${data.intitule}`
  }

  taxeDisplayExpr(data) {
    if (!data) return '';
    return data.codeBo ? `${data.codeBo} - ${data.intitule}` : data.intitule;
  }

  /** À l'activation de l'avertissement taxe, propose le message par défaut si le champ est vide. */
  onAvertissementTaxeActiveChanged = (e: { value?: boolean | null }): void => {
    if (e?.value === true && !this.tpvSettings.avertissementTaxeMessage) {
      this.tpvForm?.instance?.updateData('avertissementTaxeMessage', this.defaultMessageAvertissementTaxe);
    }
  };

  async setLieuStockageDataSource() {
    if (this.isLieuxStockageForSite && this.siteId == null) return;

    const avecFonds = this.isLieuxStockageForSite;
    this.lieuStockageDataSource = await lastValueFrom(this.lieuStockageLexiClient.rechercher(this.siteId, null, null, null, avecFonds));
  }

  appliquerPattern(pattern: string | null | undefined): string {
    if (!pattern) {
      return '';
    }

    // Syntaxe: {variable,alignement:maxLength}
    // - alignement positif = padding gauche (aligné droite)
    // - alignement négatif = padding droite (aligné gauche)
    // - maxLength = troncature
    // Exemples: {CodeArticle,10} {CodeArticle,-10} {CodeArticle:5} {CodeArticle,10:5}
    const placeholderRegex = /\{(\w+)(?:,(-?\d+))?(?::(\d+))?\}/g;

    return pattern.replaceAll(placeholderRegex, (match, variable, alignement, maxLength) => {
      // Trouver la valeur correspondante
      const colonne = this.colonnesPatternLignePanier.find(col => col.value === `{${variable}}`);
      if (!colonne) {
        return match; // Garder le pattern si non trouvé
      }

      let valeur = colonne.exemple?.toString() ?? '';

      // 1. Troncature d'abord
      if (maxLength) {
        const tailleMax = Number.parseInt(maxLength);
        if (valeur.length > tailleMax) {
          valeur = valeur.substring(0, tailleMax);
        }
      }

      // 2. Puis padding/alignement
      if (alignement) {
        const width = Math.abs(Number.parseInt(alignement));
        if (Number.parseInt(alignement) < 0) {
          // Alignement à gauche (padding à droite)
          valeur = valeur.padEnd(width, ' ');
        } else {
          // Alignement à droite (padding à gauche)
          valeur = valeur.padStart(width, ' ');
        }
      }

      return valeur;
    });
  }

  get apercuLigne1(): string {
    return this.appliquerPattern(this.tpvSettings.patternPanierLigne1) || this.pieceDetailExemple.libelleLongArticle || '';
  }

  get apercuLigne2(): string {
    return this.appliquerPattern(this.tpvSettings.patternPanierLigne2);
  }

  get apercuLigne3(): string {
    return this.appliquerPattern(this.tpvSettings.patternPanierLigne3);
  }
}